<script lang="ts" setup>
const emit = defineEmits(["close", "continue", "skip"]);
const userStore = useUserStore();
const route = useRoute();
const { $serverApi } = useNuxtApp();
const methods = ref([
  {
    name: "Google",
    id: "google" as Extract<UserKeyMethod, "google">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "google",
    enabled: true,
  },
  {
    name: "Apple",
    id: "apple" as Extract<UserKeyMethod, "apple">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "apple",
    enabled: true,
  },
  {
    name: "Facebook",
    id: "facebook" as Extract<UserKeyMethod, "facebook">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "facebook",
    enabled: true,
  },
]);
const processingLogin = ref<Extract<
  UserKeyMethod,
  "google" | "apple" | "facebook"
> | null>(null);
const linkedSocial = ref<Extract<
  UserKeyMethod,
  "google" | "apple" | "facebook"
> | null>(null);
const hasSocialsLinked = () => {
  if (
    userStore.identityProviders?.google ||
    userStore.identityProviders?.apple ||
    userStore.identityProviders?.facebook
  ) {
    if (userStore.identityProviders?.google) {
      linkedSocial.value = "google";
    } else if (userStore.identityProviders?.apple) {
      linkedSocial.value = "apple";
    } else if (userStore.identityProviders?.facebook) {
      linkedSocial.value = "facebook";
    }
    return true;
  } else return false;
};

const handleSelectedMethod = async (
  methodId: Extract<UserKeyMethod, "google" | "apple" | "facebook">
) => {
  processingLogin.value = methodId;
  if (
    methodId === "google" ||
    methodId === "apple" ||
    methodId === "facebook"
  ) {
    const currentPath = getCurrentFullPath(route.path);
    const linkactionUrl = userStore.accountAddress ? "link" : "walletless-link";
    const requestBody = {
      methodId,
      path: currentPath,
    };
    const headers = {
      Authorization: `Bearer ${useUserStore().authToken}`,
    };
    let response: any;
    try {
      response = await $serverApi(`/oauth/${linkactionUrl}/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    } catch {
      response = await $fetch(`/oauth/${linkactionUrl}/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    }
    if (response.statusCode !== 200)
      throw new Error("Failed to get oauth link");
    else navigateTo(response.payload, { external: true });
  }
};
</script>

<template>
  <div class="mt-6 mb-2 flex flex-col items-start gap-4">
    <p v-if="userStore.accountAddress" class="text-neutral-300">
      {{
        hasSocialsLinked()
          ? $t("main.keysSettings.linkedSocialAccounts")
          : $t("main.keysSettings.linkSocialToWallet")
      }}
    </p>
    <p v-else class="text-neutral-300">
      {{
        hasSocialsLinked()
          ? $t("main.keysSettings.linkedSocialAccounts")
          : $t("main.keysSettings.linkSocialAccount")
      }}
    </p>
    <div class="flex flex-col items-start gap-2 w-full">
      <p class="text-neutral-300 text-xs font-bold">
        {{ $t("main.modalUserWalletCreate.socialAccount") }}
        <span v-if="!hasSocialsLinked()" class="text-success-300">{{
          $t("main.modalUserWalletCreate.recommended")
        }}</span>
      </p>
      <div
        v-if="!hasSocialsLinked()"
        v-for="method in methods"
        :key="method.id"
        class="group flex flex-col gap-4 bg-neutral-500 rounded-xl p-4 w-full text-neutral-300 cursor-pointer hover:text-primary-500">
        <div :key="method.id" @click="handleSelectedMethod(method.id)">
          <div class="flex items-center justify-between gap-4">
            <BaseIcon :name="method.icon" class="w-7 h-7" />
            <div class="flex-1 flex flex-col">
              <p
                class="font-bold text-neutral-200 group-hover:text-primary-500">
                {{ $t(method.name, { social: $t("main." + method.id) }) }}
              </p>
              <p class="text-sm">
                {{
                  $t(method.description, { social: $t("main." + method.id) })
                }}
              </p>
            </div>
            <NuxtIcon
              v-if="processingLogin !== method.id"
              name="mdi:external-link"
              class="w-6 h-6" />
            <LoaderSpinner v-else />
          </div>
        </div>
      </div>
      <div v-if="hasSocialsLinked()" class="w-full">
        <template v-for="method in methods" :key="method.id">
          <div
            v-if="method.id === linkedSocial"
            class="group flex flex-col gap-4 bg-neutral-500 rounded-xl p-4 w-full text-neutral-300"
            :class="{
              'border border-primary-500': linkedSocial === method.id,
            }">
            <div class="flex items-center justify-between gap-4">
              <BaseIcon :name="method.icon" class="w-7 h-7" />
              <div class="flex-1 flex flex-col">
                <p class="font-bold text-neutral-200">
                  {{ $t(method.name, { social: $t("main." + method.id) }) }}
                </p>
                <p class="text-sm text-neutral-300">
                  {{ userStore.identityProviders?.[method.id]?.email }}
                </p>
              </div>
              <NuxtIcon name="mdi:tick" class="w-6 h-6 text-primary-500" />
            </div>
          </div>
        </template>
        <BaseButtonSubmit
          :text="$t('main.modalWalletSettings.close')"
          size="lg"
          class="w-full mt-4"
          @click="emit('close')" />
      </div>
    </div>
  </div>
</template>
